<template>
  <div
    v-if="initialized"
    ref="form"
    class="post-status-form"
  >
    <form
      autocomplete="off"
      @submit.prevent
      @dragover.prevent="$refs.attachments.dragOver($event)"
    >
      <div class="form-group">
        <div
          v-if="showPreview"
          class="preview-container"
        >
          <div
            v-if="!preview"
            class="preview-status"
          >
            <FAIcon
              class="fa-old-padding"
              spin
              icon="circle-notch"
            />
            {{ $t('general.loading') }}
          </div>
          <div
            v-else-if="preview.error"
            class="preview-status preview-error"
          >
            {{ preview.error }}
          </div>
          <StatusContent
            v-else
            :status="preview"
            :with-quote="!!newStatus.quote"
            class="preview-status"
            :class="{ '-refreshing': previewLoading }"
          />
        </div>
        <div class="input composer-box">
          <div
            v-if="!disableScopeSelector && !disableVisibilitySelector"
            class="composer-top"
          >
            <ScopeMenu
              :show-all="showAllScopes"
              :user-default="userDefaultScope"
              :original-scope="repliedStatus?.visibility"
              :initial-scope="newStatus.visibility"
              @change="changeVis"
            />
            <LanguageMenu
              v-if="newPostOptions"
              :model-value="newStatus.language"
              within=".composer-box"
              @update:model-value="setLanguage"
            />
          </div>
          <EmojiInput
            v-if="showSubject"
            ref="subject-input"
            v-model="newStatus.spoilerText"
            :suggest="emojiSuggestor"
            class="input form-control subject-input unstyled"
            @shown="handleEmojiInputShow"
          >
            <template #default="inputProps">
              <RichTextarea
                v-model="newStatus.spoilerText"
                single-line
                :mentions="false"
                :placeholder="$t('post_status.content_warning')"
                :disabled="posting && !optimisticPosting"
                v-bind="propsToNative(inputProps)"
                class="input form-post-subject unstyled"
                @keydown.enter="postStatus($event)"
              />
            </template>
          </EmojiInput>
          <EmojiInput
            ref="emoji-input"
            v-model="newStatus.status"
            :suggest="emojiUserSuggestor"
            :placement="emojiPickerPlacement"
            class="input form-control main-input unstyled"
            enable-sticker-picker
            enable-emoji-picker
            hide-emoji-button
            quick-picker
            picker-bound-to=".composer-box"
            :newline-on-ctrl-enter="submitOnEnter"
            @sticker-uploaded="addMediaFile"
            @sticker-upload-failed="uploadFailed"
            @shown="handleEmojiInputShow"
          >
            <template #default="inputProps">
              <RichTextarea
                ref="textarea"
                v-model="newStatus.status"
                :placeholder="placeholder || $t('post_status.default')"
                :disabled="posting && !optimisticPosting"
                class="input form-post-body"
                :style="maxHeight && { maxHeight: `${maxHeight}px` }"
                v-bind="propsToNative(inputProps)"
                @keydown.exact.enter="submitOnEnter && postStatus($event)"
                @keydown.meta.enter.prevent="postStatus($event, newStatus)"
                @keydown.ctrl.enter.prevent="!submitOnEnter && postStatus($event)"
                @input="keepBottomInView"
                @paste="paste"
              />
            </template>
          </EmojiInput>
          <div
            v-if="scheduling"
            class="schedule-row"
          >
            <FAIcon
              icon="clock"
              class="schedule-icon"
            />
            <input
              v-model="newStatus.scheduledAt"
              type="datetime-local"
              class="input schedule-input"
              :aria-label="$t('post_status.schedule_at')"
              :title="$t('post_status.schedule_at')"
              :min="earliestSchedule"
            >
            <button
              type="button"
              class="button-unstyled schedule-remove"
              :title="$t('post_status.unschedule')"
              :aria-label="$t('post_status.unschedule')"
              @click="toggleSchedule"
            >
              <FAIcon icon="times" />
            </button>
          </div>
          <div
            v-if="expiring"
            class="schedule-row expiry-row"
          >
            <FAIcon
              icon="hourglass-half"
              class="schedule-icon"
            />
            <label class="expiry-label">
              {{ $t('post_status.expire_after') }}
              <select
                v-model.number="newStatus.expiresIn"
                class="input schedule-input"
              >
                <option
                  v-for="choice in expiryChoices"
                  :key="choice.seconds"
                  :value="choice.seconds"
                >
                  {{ choice.label }}
                </option>
              </select>
            </label>
            <button
              type="button"
              class="button-unstyled schedule-remove"
              :title="$t('post_status.keep_post')"
              :aria-label="$t('post_status.keep_post')"
              @click="toggleExpiry"
            >
              <FAIcon icon="times" />
            </button>
          </div>
          <PollForm
            v-if="pollsAvailable"
            ref="pollForm"
            v-model="newStatus.poll"
            :visible="pollFormVisible"
          />
          <!-- the post this quotes; Reply turns the form back into a reply -->
          <div
            v-if="quoteThreadToggled"
            class="composer-quote"
          >
            <Quote
              :status-id="newStatus.quote.id"
              :status-url="repliedStatus?.external_url"
              status-visible
              preview
            />
          </div>
          <PostToolbar
            ref="bottom"
            :polls="pollsAvailable"
            :poll-on="pollFormVisible"
            :cw="!disableSubject"
            :cw-on="showSubject"
            :characters-left="showCounter ? charactersLeft : null"
            :posting="posting"
            :scheduled="scheduling"
            :post-disabled="isEmptyStatus || isOverLengthLimit || posting || uploadingFiles || disableSubmit"
            :menu-items="showPostMenu ? postMenuItems : []"
            @poll="togglePollForm"
            @cw="toggleSubject"
            @emoji="toggleEmojiPicker"
            @post="postStatus"
          >
            <template #upload>
              <media-upload
                ref="mediaUpload"
                class="bottom-left-button media-upload-icon"
                :drop-files="dropFiles"
                :disabled="uploadFileLimitReached"
                @uploading="startedUploadingFiles"
                @uploaded="addMediaFile"
                @upload-failed="uploadFailed"
                @all-uploaded="finishedUploadingFiles"
              />
            </template>
          </PostToolbar>
        </div>
      </div>
      <span
        v-if="!disableDraft && shouldAutoSaveDraft"
        class="auto-save-status"
      >
        {{ autoSaveState }}
      </span>
      <div
        v-if="error"
        class="alert error -dismissible"
      >
        <span>
          {{ error }}
        </span>
        <button
          :title="$t('general.dismiss')"
          :aria-label="$t('general.dismiss')"
          class="button-unstyled"
          @click="clearError"
        >
          <FAIcon
            class="fa-scale-110 fa-old-padding"
            icon="times"
          />
        </button>
      </div>
      <PostAttachments
        ref="attachments"
        v-model:files="newStatus.files"
        v-model:descriptions="newStatus.mediaDescriptions"
        v-model:nsfw="newStatus.nsfw"
        :hide-sensitive="disableSensitivityCheckbox"
        :full="uploadFileLimitReached"
        @drop="files => dropFiles = files"
        @play="$emit('mediaplay', 'newStatus')"
        @pause="$emit('mediapause', 'newStatus')"
      />
    </form>
    <DraftCloser
      ref="draftCloser"
      @save="saveAndCloseDraft"
      @discard="discardAndCloseDraft"
    />
  </div>
</template>

<script src="./post_status_form.js"></script>

<style src="./post_status_form.scss" lang="scss"></style>
